import { useCallback, useEffect, useState } from 'react'
import { storeInstance } from 'store/createStore'
import {
  getClientPublicKey,
  getUserId,
  isImpersonating,
  getIsPausedForActor,
} from 'store/modules/user-session/selectors'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { useBridgeTransfer } from 'hooks/useBridgeTransfer'
import { useCustomerConfig } from 'hooks/useCustomerConfig'
import { CustomerConfigKey } from 'modules/customer-config'
import { createErrorNotification } from 'modules/notification-manager/actions'

const SHOPPING_PAUSED_MESSAGE =
  'Shopping is temporarily unavailable while your enrollment is paused.'

// Shared guard for both bridge variants: no-op (with a notice) when paused for this actor.
const blockWhenPaused = (
  isPausedForActor: boolean,
  onError: (message: string) => void,
  transfer: (groupId?: string) => Promise<void>,
  groupId?: string
): Promise<void> => {
  if (isPausedForActor) {
    onError(SHOPPING_PAUSED_MESSAGE)
    return Promise.resolve()
  }
  return transfer(groupId)
}

type ShopForHealthBridgeState = {
  clientPublicKey: string
  employeePublicKey: string
  idToken: string
  isImpersonated: boolean
  isPausedForActor: boolean
}

type TransferringButtonType = 'plan-select' | 'new-plan' | 'qle' | null

type ShopForHealthBridge = {
  /**
   * `true` when the tenant should hand off to the new EE-UX shop instead of
   * the legacy `/benefits/health-bundles` page. Equals
   * `isEeuxEnabled || shouldUseBridge` (customer-config OR LD flag).
   * Call sites should branch on this: if `true`, invoke `bridge()`; otherwise
   * fall through to the legacy `browserHistory.push(...)`.
   */
  shouldBridge: boolean
  /**
   * Value of the per-tenant customer-config key
   * `employee-experience.enable-shopping-experience` (coerced from `null` to
   * `false`). Exposed for call sites that need it independent of the LD-flag
   * path (e.g. `GroupsView`'s `shouldShowNewPlanButton`).
   */
  isEeuxEnabled: boolean
  /**
   * Start the standard "Select A Plan" bridge transfer. POSTs to
   * `/api/v1/employee-experience/auth/bridge-login`, then does a full-page
   * navigation to the returned `redirectUrl`.
   *
   * @param groupId Optional group id appended to the redirect target as
   *   `?groupId=…`. Cart-card and Save-and-Continue call sites omit this;
   *   `GroupsView` supplies it per group.
   */
  bridge: (groupId?: string) => Promise<void>
  /**
   * Admin-only "Select A Plan (NEW)" preview bridge. Same underlying network
   * call as `bridge`, but tagged so the UI can route loading state to the
   * correct button when both variants render on the same page (Groups).
   * Caller is responsible for gating on the admin-preview conditions.
   */
  bridgeNewPlan: (groupId?: string) => Promise<void>
  /**
   * `true` while a bridge transfer is in flight (from click through
   * `window.location.href = …`). Wire to a button's `disabled`/`loading` prop
   * to prevent double-fire. Derived from `transferringButtonType` (not from
   * `useBridgeTransfer`'s `isTransferring`) so it activates for callers that
   * invoke `bridge()` without a `groupId`.
   */
  isTransferring: boolean
  /**
   * Which variant is transferring, or `null` when idle. Used by pages that
   * render more than one bridge button (Groups: "Select A Plan" vs
   * "Select A Plan (NEW)") to route loading state to the clicked button only.
   */
  transferringButtonType: TransferringButtonType
  /**
   * Group id passed to the current transfer, or `null`. Used by `GroupsView`
   * to highlight per-group loading state; omitted from cart / Save-and-
   * Continue flows because they don't pass a group id.
   */
  transferringGroupId: string | null
}

const readState = (): ShopForHealthBridgeState => {
  const state = storeInstance.getState()
  const credentials = authSelectors.getCredentials(state) || {}
  return {
    clientPublicKey: getClientPublicKey(state) || '',
    employeePublicKey: getUserId(state) || '',
    idToken: credentials.idToken || '',
    isImpersonated: Boolean(isImpersonating(state)),
    isPausedForActor: Boolean(getIsPausedForActor(state)),
  }
}

/**
 * Bridges legacy Shop-For-Health nav to the new EE-UX shop when the tenant is
 * configured for it. Wraps `useBridgeTransfer` + `useCustomerConfig` and reads
 * the required Redux fields via `storeInstance` (react-redux v5, no hooks).
 *
 * Callers should invoke `shouldBridge` before pushing to HEALTH_PRODUCTS_PATH:
 *
 *   if (shouldBridge) { bridge(groupId); return }
 *   browserHistory.push(HEALTH_PRODUCTS_PATH)
 */
export const useShopForHealthBridge = (): ShopForHealthBridge => {
  const [reduxState, setReduxState] = useState<ShopForHealthBridgeState>(readState)

  useEffect(() => {
    const sync = () => {
      const next = readState()
      setReduxState((prev) =>
        prev.clientPublicKey === next.clientPublicKey &&
        prev.employeePublicKey === next.employeePublicKey &&
        prev.idToken === next.idToken &&
        prev.isImpersonated === next.isImpersonated &&
        prev.isPausedForActor === next.isPausedForActor
          ? prev
          : next
      )
    }
    const unsubscribe = storeInstance.subscribe(sync)
    sync()
    return unsubscribe
  }, [])

  const onError = useCallback((message: string) => {
    storeInstance.dispatch(createErrorNotification(message))
  }, [])

  const { data: eeuxConfig } = useCustomerConfig(
    reduxState.clientPublicKey,
    CustomerConfigKey.EMPLOYEE_EXPERIENCE__ENABLE_SHOPPING_EXPERIENCE
  )
  const isEeuxEnabled = eeuxConfig ?? false

  const {
    handleBridgeTransfer,
    handleNewPlanBridgeTransfer,
    shouldUseBridge,
    transferringButtonType,
    transferringGroupId,
  } = useBridgeTransfer({
    idToken: reduxState.idToken,
    employeePublicKey: reduxState.employeePublicKey,
    isImpersonated: reduxState.isImpersonated,
    clientPublicKey: reduxState.clientPublicKey,
    onError,
  })

  const shouldBridge = isEeuxEnabled || shouldUseBridge
  // Derive from `transferringButtonType` (always set at transfer start) rather
  // than `useBridgeTransfer`'s own `isTransferring` (which is `transferringGroupId !== null`
  // and stays false when callers invoke `bridge()` without a groupId — e.g., Cart cards
  // and SaveAndContinueButton).
  const isTransferring = transferringButtonType !== null

  // Feature-block: while the enrollment is paused for this actor, block the hand-off to the
  // EE-UX shop-health-plans page from every entry point (Groups, cart cards, Save-and-Continue).
  // Exemption-aware, so exempt actors still bridge normally.
  const isPausedForActor = reduxState.isPausedForActor
  const bridge = useCallback(
    (groupId?: string) => blockWhenPaused(isPausedForActor, onError, handleBridgeTransfer, groupId),
    [isPausedForActor, onError, handleBridgeTransfer]
  )
  const bridgeNewPlan = useCallback(
    (groupId?: string) =>
      blockWhenPaused(isPausedForActor, onError, handleNewPlanBridgeTransfer, groupId),
    [isPausedForActor, onError, handleNewPlanBridgeTransfer]
  )

  return {
    shouldBridge,
    isEeuxEnabled,
    bridge,
    bridgeNewPlan,
    isTransferring,
    transferringButtonType,
    transferringGroupId,
  }
}
